/**
 * Section tab strips — the second half of the "Simplified navigation" design.
 *
 * The design flattens the sidebar to one entry per section and moves the
 * sub-navigation *into* each page as a tab strip. `dhruvaMain.js` does the
 * flattening; this file supplies the tabs.
 *
 * These tabs NAVIGATE. Every one points at a route that already exists, so each
 * page keeps its own component, its own data fetching and its own lifecycle —
 * nothing here merges pages or fetches anything. That is why flattening the
 * sidebar did not orphan the ~13 menu entries it dropped: they are all reachable
 * again through these strips.
 *
 * Sector and Admin are deliberately absent, because the design renders no strip
 * for a single-tab section (`.tabs:empty{display:none}`). So is every other
 * route in the app, so the strip only appears where the design puts one.
 *
 * Labels are plain strings, not `<FormattedMessage>`. The bundled locale files
 * carry only the template's demo keys, so the menu's ids like "Hall of Fame"
 * have always rendered via react-intl's missing-message fallback — which is the
 * id itself. Plain strings give the same output without the console warnings.
 */

/*
 * `title` and `sub` are the design's `.phead` — the section heading rendered
 * above the tab strip. They are the design's own strings, taken from its PAGES
 * map, and they sit where the design puts them: above the tabs, with no page
 * title underneath, because the pages' own headings are card headings
 * ("Team Dashboard", "Market Share"), not page ones.
 *
 * `icon` is the mark in the heading's circular badge; each tab's `icon` is the
 * glyph before its label. They are components, not names, so a typo is a build
 * error rather than a blank square at runtime. Names and routes are unchanged
 * by the icons — this is presentation only.
 *
 * `info` is the line behind a tab's info glyph, read in a tooltip over the label
 * it belongs to. Two surfaces draw that glyph — SectionTabs draws the section's
 * pills, ModulePage draws the module's three views — and both read this field,
 * so the line is a property of the tab rather than of either strip. Events and
 * TF Masters carry one for the switcher alone: they left the section's row, so
 * nothing but the switcher has a label to put it on. The line answers what the
 * label leaves open — it is not a rewording of the label — and stays short
 * enough not to wrap in a tooltip.
 */
import {
    BookOpen,
    Bot,
    Briefcase,
    Calendar,
    ClipboardList,
    Compass,
    Crown,
    Eye,
    FileText,
    FlaskConical,
    GraduationCap,
    Heart,
    Hourglass,
    Inbox,
    LayoutGrid,
    Library,
    LifeBuoy,
    PieChart,
    Rocket,
    Star,
    Ticket,
    Trophy,
    Users
} from 'lucide-react';

/*
 * A tab is declared once and referenced wherever it belongs. The same object is
 * both a pill in a row and an entry in the sidebar's submenu (dhruvaMain.js's
 * tabsOf), so two copies of one tab could drift apart while one object cannot.
 *
 * Community is why these are named: its pills are also the sidebar's Community
 * submenu (tabsOf reads them), so the row, the url and the sidebar entry are one
 * declaration rather than three that have to agree. The module's own three views
 * are named for the same reason even though they are no longer in that row — the
 * switcher inside the page reads them, and so does moduleHref, which the two
 * redirect routes resolve through.
 */

/*
 * The Dashboard module's page, and the three views of it. All three module tabs
 * are the SAME page: what differs is the `tab` query key, so switching views is a
 * change of query string, not of route.
 *
 * `tab` is the view key, the same shape `year` already has for QIC, and `tabHref`
 * serialises both. TAB_DASHBOARD deliberately has no `tab`, so the default view
 * keeps the plain `/CommunityDashboard` and the bare URL stays canonical.
 *
 * `viewLabel` is the one field here that is not part of a destination's identity.
 * These three left the section's row — the module's page renders them itself now
 * — so one object feeds more than one surface: the in-page switcher for all
 * three, and the section's row and the sidebar's submenu for TAB_DASHBOARD. The
 * design names the default view "Overview" in the switcher while the pill and the
 * sidebar row go on naming the destination "Dashboard", so the switcher's label
 * is stated separately.
 *
 * Only TAB_DASHBOARD carries one. Hall of Fame and TF Masters read the same either
 * way, and a second field holding a copy of `label` is a field that can drift out
 * of step with it. `tabHref` ignores `viewLabel`, so the sidebar's id and url are
 * untouched by it.
 */
export const DASHBOARD_MODULE_PATH = '/CommunityDashboard';

const TAB_DASHBOARD = {
    label: 'Dashboard',
    viewLabel: 'Overview',
    path: DASHBOARD_MODULE_PATH,
    icon: LayoutGrid,
    info: 'Community Activities.'
};
// Events and Hall of Fame have traded places. Events left the module's switcher
// for Community's own row and took a route of its own at /Events, where its page
// renders directly; Hall of Fame took the switcher slot Events gave up and is now
// a view of /CommunityDashboard, with /Hall_Of_Fame redirecting into it. Each one
// carries its own label, icon and info line to its new home — a tab and its
// content travel together, so no label ever sits over the other module's page.
const TAB_EVENTS = {
    label: 'Events',
    path: '/Events',
    icon: Calendar,
    // "Online Events" is the view's own heading, which is the word to use when
    // the label says only where you are, not what you find there.
    info: 'Online events and your attendance.'
};
const TAB_TF_MASTERS = {
    label: 'TF Masters',
    path: DASHBOARD_MODULE_PATH,
    tab: 'tf-masters',
    icon: Trophy,
    // The view is a voting table and, under it, the winner that table produces.
    info: 'The votes, and who won the week.'
};
const TAB_HALL_OF_FAME = {
    label: 'Hall of Fame',
    path: DASHBOARD_MODULE_PATH,
    tab: 'hall-of-fame',
    icon: Crown,
    info: 'Members who reached each milestone.'
};

/**
 * The three views of the Dashboard module's page, in the switcher's order.
 *
 * Not a section row. These sit in the tab strip the module's page renders above
 * itself, where they read as views of one page rather than as siblings of it.
 * ModulePage maps this list to that strip. Events is not among them: it is a page
 * of its own now, so it is a tab on Community's row instead.
 *
 * This list is what `?tab=` is validated against, and what the /Hall_Of_Fame and
 * /TFMasters redirects resolve through. A view added to the page without being
 * added here would be unreachable by URL and would light no tab.
 */
export const DASHBOARD_VIEW_TABS = [TAB_DASHBOARD, TAB_HALL_OF_FAME, TAB_TF_MASTERS];

const SECTIONS = [
    {
        id: 'community',
        title: 'Community',
        sub: 'Your rewards, your group and the people around you',
        icon: Users,
        // The section's row — and, through tabsOf, the sidebar's Community
        // submenu. Two destinations: Dashboard is the module's page, and Events is
        // a page of its own. TF Masters is a view *of* the module's page rather
        // than a page beside it, so it sits in the switcher inside the page, where
        // DASHBOARD_VIEW_TABS lists it — Hall of Fame joined it there when the two
        // traded places with Events.
        tabs: [TAB_DASHBOARD, TAB_EVENTS],
        // Those views are still Community URLs, and this list is the ONLY reason
        // the ones that resolve nowhere else reach this section. /TFMasters is the
        // clearest case: its tab's path is the module's path, so getSectionTabs
        // pass 2 can never match it, and pass 3 reading this list is all that
        // stands between a visit there and the header card blinking out for the
        // redirect frame. /Hall_Of_Fame is the same story, its page now being a
        // view of the module's. Drop these entries and the header card flickers on
        // every /Hall_Of_Fame and /TFMasters link in the app.
        //
        // INVARIANT: every path in `tabs` must appear here too. A tab missing
        // from this list still matches by its exact path, but silently stops
        // matching its own sub-paths — so a section that declares `paths` at all
        // declares all of them. The converse is not a violation: a path may be
        // listed with no tab of its own, which is exactly what these two are.
        paths: ['/CommunityDashboard', '/Events', '/Hall_Of_Fame', '/TFMasters']
    },
    {
        id: 'satellite',
        title: 'Satellite',
        sub: 'Candidates on the way to Core',
        icon: Rocket,
        tabs: [
            // One glyph each, no two alike. Nakshatra keeps the star because
            // that is what the word means; My List took a list, which is also
            // what the admin submenu's My List already wears. The two used to
            // share `Star`, so the row read as two of one thing.
            //
            // The two watchlist lines below are a pair: one is the list a
            // member reads, the other the one they build. The window each
            // names is the page's own — /mylist pivots four calendar weeks,
            // /SateliteWatchlist carries a year and a week picker.
            {
                label: 'Watchlist',
                path: '/mylist',
                icon: Eye,
                info: 'Latest stocks from the past 4 weeks.'
            },
            {
                label: 'Nakshatra',
                path: '/nakshatra',
                icon: Star,
                beta: false,
                info: 'Discover stocks by their stage.'
            },
            {
                label: 'My List',
                path: '/SateliteWatchlist',
                icon: ClipboardList,
                beta: false,
                info: 'Build your weekly stock list.'
            },
            {
                label: 'Pratiksha',
                path: '/pratiksha',
                icon: Hourglass,
                info: 'Your watchlist, plus what others hold.'
            }
        ]
    },
    {
        id: 'core',
        title: 'Core',
        sub: 'Your conviction holdings, how they are performing, and what comes next',
        icon: Briefcase,
        tabs: [
            {
                label: 'Watchlist',
                path: '/CoreWatchlist',
                icon: Eye,
                info: 'TA & FA stock insights.'
            },
            {
                label: 'Audit',
                path: '/AllCore',
                icon: PieChart,
                // No cadence here. The page reads the quarter it is already
                // in — `financialQuarter()` from today's date, with no picker
                // to choose another (CoreAudit.js:320-325) — so a line naming
                // a quarter would promise a choice nobody is offered.
                info: 'Build your portfolio.'
            }
            // The design also lists a "Portfolio Builder" tab, but it has no
            // route: it is a component embedded *inside* the Core Audit page
            // (CoreAudit.js renders PortfolioBuilder/PortfolioSteps). The
            // design's own note says it "was a button inside that screen",
            // which is exactly its situation here. Omitted rather than pointed
            // somewhere arbitrary.
        ]
    },
    {
        id: 'research',
        title: 'Research',
        sub: 'What you are studying, before it becomes a position',
        icon: FlaskConical,
        tabs: [
            {
                label: 'Reports',
                path: '/ResearchReport',
                icon: FileText,
                info: 'Company notes with a Buy or Sell call.'
            }
        ]
    },
    {
        id: 'Growth Hub',
        title: 'Growth Hub',
        sub: 'Coaches, books and practices',
        icon: GraduationCap,
        tabs: [
            {
                label: 'University',
                path: '/UniverseCoach',
                icon: GraduationCap,
                info: 'Find a coach and see their profile.'
            },
            {
                label: 'Book Club',
                path: '/bookClub',
                icon: BookOpen,
                info: 'Books to read, and daily events.'
            },
            {
                label: 'Wellness',
                path: '/WellnessProgram',
                icon: Heart,
                info: 'Your Daily Yoga Space.'
            },
            // The design folds the old "My Books" and "References" entries into
            // one Library tab; /Resources is the surviving route. It must stay
            // the ONLY tab on that path — a second one would share its React
            // key, and React would then reconcile the two onto one element and
            // light up both pills as active.
            {
                label: 'Resources',
                path: '/Resources',
                icon: Library,
                info: 'A Hub for Tools & Resources.'
            }
        ]
    },
    {
        id: 'qic',
        title: 'Quantum Investing Champion',
        sub: 'Annual championship',
        // QIC is the one section whose tabs are not a fixed list: it runs one
        // championship per year, so its tabs are the event years actually in the
        // database — built at render time by buildYearTabs() from the same
        // fetchEvents the QIC pages use (store/slices/qic), rather than written
        // out here where they would go stale every January.
        //
        // `yearTabs` marks them as year variants of one section rather than
        // distinct pages, which the matcher and the active-tab lookup both need
        // to know. `yearPrefix` is what the label is built from — QIC2026, not
        // a bare 2026.
        yearTabs: true,
        yearPrefix: 'QIC',
        icon: Trophy,
        // The year tabs are built from data, so they have no entry here to hang
        // an icon on — the section carries it instead.
        yearIcon: Trophy,
        // Same reason, and the same answer: every year tab opens the one page
        // with a different year, so the line is the section's, not each year's.
        yearInfo: 'Switch the championship year.',
        tabs: [],
        // Where the section actually lives. The year travels in `?year=` and the
        // same pages are reachable at several paths, so the tabs cannot describe
        // the section's extent on their own — this is what the matcher uses
        // instead, and what buildYearTabs() points the year tabs back at so a
        // year click stays on the page you are reading.
        //
        // Full paths, deliberately, not a bare '/qic': that would also swallow
        // the admin routes (/qic_admin, /qic_event, /qic/setting_qicvotes) and
        // suppress their admin strip, which is the bug fixed for /ResearchReport.
        //
        // The event stages are deliberately absent: they are steps inside the
        // event flow, not year-scoped destinations, so a year tab there falls
        // back to the canonical dashboard path.
        paths: ['/qic/dashboard', '/qic/dashboard2025', '/qic/dashboard/event', '/qic/dashboard/winners', '/User/Qic/Team']
    },
    {
        id: 'help',
        title: 'Help',
        sub: 'Powered by DhruvDoot · answers instantly',
        icon: LifeBuoy,
        // Four different ways to ask for something, so the labels alone do not
        // say which one takes which kind of message — which is what each tab's
        // `info` line is here to answer. The other sections carry one too now;
        // see the field's note at the top of this file.
        tabs: [
            {
                label: 'Onboarding',
                path: '/Onboarding',
                icon: Compass,
                info: 'Track your setup progress.'
            },
            {
                label: 'Raise a ticket',
                path: '/contact-us',
                icon: Ticket,
                info: 'Raise a new issue or query.'
            },
            {
                label: 'My tickets',
                path: '/HelpResponse',
                icon: Inbox,
                info: 'Track tickets you have raised.'
            },
            {
                label: 'Ask Chanakya',
                path: '/ChatbotZapier',
                icon: Bot,
                info: 'Technofunda-related queries.'
            }
        ]
    }
];

/*
 * The two query keys a tab can be told apart by. `year` is QIC's — one page whose
 * tabs are years. `tab` is the Dashboard module's — one page whose tabs are
 * views. Read together, so `matches` answers one question: which of these tabs is
 * the URL describing?
 */
const queryOf = (search) => {
    const params = new URLSearchParams(search || '');
    return { year: params.get('year'), tab: params.get('tab') };
};

/*
 * A tab matches a URL only when it pins the same values that URL carries — and a
 * tab that pins neither matches only a URL that carries neither. So
 * /qic/dashboard?year=2024 never lights the 2026 tab, and
 * /CommunityDashboard?tab=tf-masters never lights Events even though it is the
 * Dashboard pill's own path: Events pins a different path entirely, and the
 * module's views are queried off the Dashboard's path, not that tab's.
 */
const matches = (tab, pathname, query) =>
    tab.path === pathname &&
    (tab.year || null) === (query.year || null) &&
    (tab.tab || null) === (query.tab || null);

// Sub-pages (e.g. /qic/dashboard/event/stage1) belong to the section that owns
// their closest ancestor path.
const isUnder = (tab, pathname) => pathname.startsWith(`${tab.path}/`);

const longestPrefix = (tabs, pathname) => {
    let index = -1;
    let length = -1;
    tabs.forEach((tab, i) => {
        if (isUnder(tab, pathname) && tab.path.length > length) {
            length = tab.path.length;
            index = i;
        }
    });
    return index;
};

const sectionWhere = (predicate) => SECTIONS.find((section) => section.tabs.some(predicate));

/**
 * Every path a section owns. Usually just its tabs' paths, and only two sections
 * declare their own `paths` — for opposite reasons.
 *
 * QIC's tabs are year variants of one page and so describe nothing about extent,
 * which is what its `paths` is for. Community's tabs describe too LITTLE on
 * purpose: two of its destinations moved into the Dashboard module's row, so
 * `paths` is what keeps them the section's after they left its row.
 */
const pathsOf = (section) => section.paths || section.tabs.map((tab) => tab.path);

/**
 * The section owning `pathname`, or null when the route has no strip.
 *
 * Four passes, loosening each time. The middle one matters: `/qic/dashboard2025`
 * reached WITHOUT its `?year=2025` — a bookmark, or a redirect that drops the
 * query — fails the year-aware match, and without the path-only pass it would
 * belong to no section and the page would render no strip at all, stranding the
 * user with no way back to QIC's other tabs.
 */
export const getSectionTabs = (pathname, search) => {
    const query = queryOf(search);

    const exact = sectionWhere((tab) => matches(tab, pathname, query));
    if (exact) return exact;

    const byPath = sectionWhere((tab) => tab.path === pathname);
    if (byPath) return byPath;

    // A section that declares its own paths is matched on those. This is what
    // carries QIC, whose tabs are not in this file at all, and what carries the
    // Dashboard module's views — /TFMasters and now /Hall_Of_Fame — whose tabs
    // wear the module's path rather than their own.
    const byOwnedPath = SECTIONS.find((section) => pathsOf(section).includes(pathname));
    if (byOwnedPath) return byOwnedPath;

    let best = null;
    let bestLength = -1;
    SECTIONS.forEach((section) => {
        pathsOf(section).forEach((path) => {
            if (isUnder({ path }, pathname) && path.length > bestLength) {
                bestLength = path.length;
                best = section;
            }
        });
    });
    return best;
};

/** Index of the tab to highlight, falling back to path-only then sub-path. */
export const getActiveIndex = (section, pathname, search) => {
    if (!section) return -1;
    const query = queryOf(search);
    let i = section.tabs.findIndex((tab) => matches(tab, pathname, query));

    // A year-variant section (QIC) is ONE page whose tabs are years, so every
    // tab shares a path and the path fallbacks below would always land on the
    // first. Match the year instead. With no year in the URL the newest is the
    // right default, being what a bare path serves.
    if (i === -1 && section.yearTabs) {
        i = query.year ? section.tabs.findIndex((tab) => String(tab.year) === String(query.year)) : 0;
    }

    // The path fallback lands on the FIRST tab sharing that path, which for
    // Community is Dashboard. That is the same answer moduleViewOf gives for an
    // absent or unrecognised `?tab=`, so an unparseable URL lights Dashboard
    // above the Dashboard body rather than no pill above it.
    if (i === -1) i = section.tabs.findIndex((tab) => tab.path === pathname);
    if (i === -1) i = longestPrefix(section.tabs, pathname);
    return i;
};

/**
 * QIC's tabs: one per event year, every one pointing at the page you are
 * already on with the year in `?year=`. That is how the QIC pages read the year
 * back out, so a year click filters in place instead of throwing you back to the
 * dashboard — the dashboard itself links out to /qic/dashboard/event?year=… and
 * /User/Qic/Team?year=…, and tabs pinned to a single canonical path would drop
 * you out of whichever page you were reading.
 *
 * `years` arrives from the database via store/slices/qic, newest first, and
 * `events` is the same fetch's event rows. Those carry the `status`, which is
 * where `live` comes from: the championship running right now, or the next one
 * already announced as "Coming Soon", so the strip can mark that one year the
 * way the old in-page menu's green dot did.
 *
 * Only that flag is carried over — this returns data, not presentation. Whether
 * a dot is what marks it is the strip's business.
 */
export const buildYearTabs = (section, years = [], pathname = '', events = []) => {
    const own = pathsOf(section);
    const path = own.includes(pathname) ? pathname : own[0];
    return years.map((year) => {
        const event = events.find((e) => {
            const y = e?.event_year ?? e?.eventYear;
            return y !== undefined && String(y) === String(year);
        });
        return {
            label: `${section.yearPrefix || ''}${year}`,
            path,
            year: String(year),
            icon: section.yearIcon,
            info: section.yearInfo,
            live: ['active', 'coming soon'].includes(
                String(event?.status || '').toLowerCase().trim()
            )
        };
    });
};

/**
 * The link target for a tab: its path plus the query that identifies WHICH tab it
 * is, when the path alone does not. A year tab carries `year`, a Dashboard module
 * tab carries `tab`, and a plain tab carries neither and keeps a bare path.
 *
 * Built through URLSearchParams and appended only when non-empty, so a tab with
 * no query is byte-for-byte the path it always was.
 */
export const tabHref = (tab) => {
    const params = new URLSearchParams();
    if (tab.year) params.set('year', tab.year);
    if (tab.tab) params.set('tab', tab.tab);
    const query = params.toString();
    return query ? `${tab.path}?${query}` : tab.path;
};

/*
 * Which view of the Dashboard module is open.
 *
 * One resolver, one caller now: ModulePage picks the body with it, and lights the
 * switcher from the same answer — the switcher's index is derived from this, NOT
 * from getActiveIndex. getActiveIndex reads the SECTION's row, and these views
 * have left it, so it would answer 0 for all three and light "Overview" above the
 * Events body. A lit pill over the wrong body is the failure this file exists to
 * prevent, so the two are not allowed to be two parsers of one question.
 *
 * Absent AND unrecognised both fall back to the main view. An unknown value is a
 * stale bookmark, and answering it with a blank body under no lit pill would be
 * a worse lie than showing the Dashboard.
 */
export const moduleViewOf = (search) => {
    const view = new URLSearchParams(search || '').get('tab');
    return DASHBOARD_VIEW_TABS.some((tab) => tab.tab === view) ? view : 'main';
};

/** The link to one module view by key — so the redirect routes name no literal. */
export const moduleHref = (view) =>
    tabHref(DASHBOARD_VIEW_TABS.find((tab) => (tab.tab || 'main') === view) || TAB_DASHBOARD);

export default SECTIONS;
